import { FiUserPlus, FiShield, FiGrid, FiUsers } from "react-icons/fi";
import type { HelpTopic } from "newui/components/ui/Help";
import { buildUserListSteps, type UserListSteps } from "./userListSteps";
import { buildManageRolesSteps, type ManageRolesSteps } from "./manageRolesSteps";
import { buildViewAccessSteps, type ViewAccessSteps } from "./viewAccessSteps";
import { buildAllocateRolesSteps, type AllocateRolesSteps } from "./allocateRolesSteps";

/**
 * PRO-730 — the User Management page's Help icon opens a topic chooser
 * ("What would you like to know more about?") instead of a single long tour.
 * Each topic below is one of the page's guided flows; `UserListPage` wires
 * the openers/closers the same way it already does for `openInviteDrawer` /
 * `closeInviteDrawer`.
 *
 * "Manage a user's roles", "View a user's access" and "Allocate roles to
 * several users" only make sense — and only have anything to point at —
 * when RBAC v2's role-management surfaces are actually rendered, so callers
 * pass `rbacEnabled` to include/exclude them; "Invite a user" has no such
 * dependency and is always offered.
 */
export type UserManagementTopicsOptions = UserListSteps &
	ManageRolesSteps &
	ViewAccessSteps &
	AllocateRolesSteps & {
		/** Include the RBAC v2 topics (manage roles / view access / allocate roles). */
		rbacEnabled: boolean;
	};

export const buildUserManagementTopics = ({
	openInviteDrawer,
	closeInviteDrawer,
	openManageRoles,
	closeManageRoles,
	openViewAccess,
	closeViewAccess,
	openAllocateRoles,
	advanceWizardToScopeStep,
	closeAllocateRoles,
	rbacEnabled,
}: UserManagementTopicsOptions): HelpTopic[] => {
	const topics: HelpTopic[] = [
		{
			id: "invite-user",
			title: "Invite a user",
			description: "Add a new teammate to your company.",
			icon: <FiUserPlus size={18} />,
			steps: buildUserListSteps({ openInviteDrawer, closeInviteDrawer }),
		},
	];

	if (rbacEnabled) {
		topics.push(
			{
				id: "manage-roles",
				title: "Manage a user's roles",
				description: "Assign or remove a single user's company-wide roles.",
				icon: <FiShield size={18} />,
				steps: buildManageRolesSteps({ openManageRoles, closeManageRoles }),
			},
			{
				id: "view-access",
				title: "View a user's access",
				description: "See a user's full, resolved access, module by module.",
				icon: <FiGrid size={18} />,
				steps: buildViewAccessSteps({ openViewAccess, closeViewAccess }),
			},
			{
				id: "allocate-roles",
				title: "Allocate roles to several users",
				description: "Assign the same role or template to many users at once.",
				icon: <FiUsers size={18} />,
				steps: buildAllocateRolesSteps({
					openAllocateRoles,
					advanceWizardToScopeStep,
					closeAllocateRoles,
				}),
			},
		);
	}

	return topics;
};
